import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '@/lib/api'
import { Wallet, Loader2, Sparkles, TrendingUp, Info, Target } from 'lucide-react'
import type { PortfolioFit } from '@/lib/types'
import { PortfolioFitBadge, PortfolioFitNote } from '@/components/ui/PortfolioFitBadge'
import PageHeader from '@/components/ui/PageHeader'
import ErrorAlert from '@/components/ui/ErrorAlert'
import { fmtGBP, fmtPct, fmtNum } from '@/lib/format'

/**
 * "How should I invest £X?" — the front door.
 *
 * A real-data answer to the question a household actually asks, grounded in
 * the user's own book: idle cash per account, allowances-used-up aware, with
 * an ordered deployment plan. The opposite of asking a generic chatbot that
 * knows none of your positions, allowances, or concentration.
 */

type IdleAccount = {
  account_id: number
  account_name: string
  owner: string
  wrapper: string
  discretionary: boolean
  accessible: boolean
  idle_cash_gbp: number
  interest_rate_pct: number | null
}

type Step = {
  priority: number
  wrapper: string
  amount_gbp: number
  account_hint?: string
  why: string
  tax_note: string
}

type AllocRow = { asset_class: string; current_pct: number; target_pct: number; gap_pct: number; current_gbp: number }
type Allocation = {
  total_gbp: number
  by_class: AllocRow[]
}

type Buy = {
  asset_class: string
  amount_gbp: number
  instrument: { ticker: string; name: string; held: boolean; price: number }
  current_pct: number
  target_pct: number
  gap_pct: number
  bull_case: string
  bear_case: string
  verdict: string
  llm_enriched?: boolean
  portfolio_fit?: PortfolioFit | null
}

type Plan = {
  amount_gbp: number
  source: string
  total_idle_gbp: number
  requested_gbp?: number | null
  capped?: boolean
  allowances: Record<string, { remaining?: number; annual_limit?: number; used_pct?: number }>
  idle_accounts: IdleAccount[]
  allocation?: Allocation | null
  buys?: Buy[]
  steps: Step[]
  headline: string
}

/**
 * Compact hero for the Command Centre top-fold: one box that takes a £ amount
 * and routes to the full answer page. The actual "front door" — designed so a
 * non-expert (the question that sent someone to ChatGPT instead) lands here
 * first.
 */
export function DeployCashHero() {
  const [amount, setAmount] = useState('')
  const [idle, setIdle] = useState<number | null>(null)

  useEffect(() => {
    let alive = true
    api.get<{ deployable_gbp: number }>('/api/deploy-cash/idle')
      .then(r => { if (alive) setIdle(r.data?.deployable_gbp ?? null) })
      .catch(() => { /* hero is best-effort */ })
    return () => { alive = false }
  }, [])

  const navigate = useNavigate()
  const go = (e: React.FormEvent) => {
    e.preventDefault()
    const n = parseFloat(amount.replace(/[£,\s]/g, ''))
    const q = Number.isFinite(n) && n > 0 ? `?amount=${n}` : ''
    navigate(`/app/deploy-cash${q}`)
  }

  return (
    <form onSubmit={go} className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] to-transparent p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
        <Sparkles size={16} className="text-amber-400" />
        How should I invest my cash?
      </div>
      <div className="flex gap-2 mt-2">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm">£</span>
          <input
            value={amount}
            onChange={e => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder={idle != null ? `${Math.round(idle).toLocaleString()} idle` : '2,000'}
            className="input w-full pl-7 text-sm"
            aria-label="Amount to invest"
          />
        </div>
        <button type="submit" className="btn-primary text-sm px-4">Answer</button>
      </div>
      {idle != null && idle > 0 && (
        <p className="text-xs text-text-muted mt-2">{fmtGBP(idle)} idle cash detected across your accounts — get a deployment plan grounded in your real book.</p>
      )}
    </form>
  )
}

const WRAPPER_TONE: Record<string, string> = {
  ISA: 'text-emerald-400 border-emerald-500/40',
  SIPP: 'text-sky-400 border-sky-500/40',
  GIA: 'text-amber-400 border-amber-500/40',
  BANK: 'text-text-muted border-border-light',
}

// Plain-English expansions for the wrapper badges (shown as title tooltips).
const WRAPPER_HELP: Record<string, string> = {
  ISA: 'ISA (Individual Savings Account) — tax-free wrapper, £20k/yr allowance',
  SIPP: 'SIPP (Self-Invested Personal Pension) — tax relief on the way in, locked until pension age',
  GIA: 'GIA (General Investment Account) — no tax shelter; gains and dividends are taxable',
  BANK: 'Bank account — plain cash, outside any investment wrapper',
}

export default function DeployCashPage() {
  const [plan, setPlan] = useState<Plan | null>(null)
  const [searchParams] = useSearchParams()
  const [amount, setAmount] = useState<string>(() => {
    // The Command Centre hero routes here with ?amount=N — seed the form
    // from it (it used to be silently dropped: the page planned ALL idle
    // cash regardless of what the user typed).
    const q = parseFloat(searchParams.get('amount') ?? '')
    return Number.isFinite(q) && q > 0 ? String(q) : ''
  })
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async (amt?: number) => {
    setLoading(true)
    setErr(null)
    try {
      const { data } = await api.get<Plan>('/api/deploy-cash/plan', {
        params: amt != null && amt > 0 ? { amount: amt } : {},
      })
      setPlan(data)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Failed to build plan')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const q = parseFloat(searchParams.get('amount') ?? '')
    load(Number.isFinite(q) && q > 0 ? q : undefined)
    // mount-only: the in-page form drives subsequent loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const n = parseFloat(amount.replace(/[£,\s]/g, ''))
    load(Number.isFinite(n) && n > 0 ? n : undefined)
  }

  const isaRoom = plan?.allowances?.isa?.remaining ?? 0
  const sippRoom = plan?.allowances?.sipp?.remaining ?? 0
  // Distinguish "allowance data missing" (backend returns {} on failure)
  // from a real £0 remaining — a transient service error must never be
  // presented as the tax fact "your allowances are used up".
  const allowancesKnown = plan?.allowances?.isa != null || plan?.allowances?.sipp != null
  const allowancesGone = allowancesKnown && isaRoom < 1 && sippRoom < 1

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto">
        <PageHeader
          title="How should I invest my cash?"
          icon={Wallet}
          iconClassName="text-amber-400"
          subtitle="An ordered deployment plan grounded in your real accounts, allowances and holdings."
        />

        {/* Ask box */}
        <form onSubmit={submit} className="card p-5">
          <label className="text-sm text-text-secondary">Amount to invest (leave blank to use all idle cash)</label>
          <div className="flex gap-2 mt-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">£</span>
              <input
                value={amount}
                onChange={e => setAmount(e.target.value)}
                inputMode="decimal"
                placeholder="2,000"
                className="input w-full pl-7"
              />
            </div>
            <button type="submit" className="btn-primary flex items-center gap-2 px-5">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              Answer
            </button>
          </div>
          <p className="text-xs text-text-muted mt-2">
            Grounded in your real accounts, allowances and holdings — not a generic suggestion.
          </p>
        </form>

        {err && <ErrorAlert title="Couldn't build the plan" message={err} onRetry={() => load()} />}

        {plan && !err && (
          <>
            {/* Headline */}
            <div className="card border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] to-surface p-5">
              <div className="flex items-center gap-2 text-amber-400 text-lg font-semibold">
                <TrendingUp size={20} /> {plan.headline}
              </div>
              <p className="stat-hero text-text-primary mt-2">{fmtGBP(plan.total_idle_gbp)}</p>
              <div className="text-sm text-text-secondary mt-1">
                total idle cash detected
                {allowancesKnown
                  ? <> · ISA room {fmtGBP(isaRoom)} · SIPP room {fmtGBP(sippRoom)}</>
                  : <> · allowance data unavailable right now</>}
              </div>
              {plan.capped && plan.requested_gbp != null && (
                <div className="text-xs text-text-muted mt-1">
                  You asked about {fmtGBP(plan.requested_gbp)}, but only {fmtGBP(plan.amount_gbp)} is
                  sitting idle — the plan covers what's actually available.
                </div>
              )}
              {allowancesGone && (
                <div className="flex items-start gap-2 text-xs text-text-muted mt-3 border-t border-border/30 pt-3">
                  <Info size={14} className="mt-0.5 shrink-0" />
                  This year's ISA &amp; pension allowances are used up, so the plan deploys cash where it already
                  sits rather than chasing wrapper room. Allowances reset on 6 April.
                </div>
              )}
            </div>

            {/* Allocation snapshot — current vs target by class, so the
                "why this class" is visible at a glance. */}
            {plan.allocation && plan.allocation.by_class.length > 0 && (
              <div className="card p-5">
                <div className="panel-title mb-2">Your allocation vs target</div>
                <div className="space-y-1.5">
                  {plan.allocation.by_class.map(a => {
                    const under = a.gap_pct > 3
                    const over = a.gap_pct < -3
                    return (
                      <div key={a.asset_class} className="flex items-center gap-2 text-xs">
                        <span className="w-32 shrink-0 text-text-secondary truncate">{a.asset_class}</span>
                        <div className="flex-1 h-2 rounded-full bg-surface-hover relative overflow-hidden">
                          <div className="absolute inset-y-0 left-0 rounded-full bg-blue-light/60" style={{ width: `${Math.min(100, a.current_pct)}%` }} />
                          {/* target marker */}
                          <div className="absolute inset-y-0 w-0.5 bg-text-muted" style={{ left: `${Math.min(100, a.target_pct)}%` }} />
                        </div>
                        <span className="w-12 text-right tabular-nums text-text-secondary">{fmtPct(a.current_pct, 0)}</span>
                        <span className={`w-16 text-right tabular-nums ${under ? 'text-emerald-400' : over ? 'text-rose-400' : 'text-text-muted'}`}>
                          {under ? `+${fmtNum(a.gap_pct, 0)}% room` : over ? `${fmtNum(a.gap_pct, 0)}% over` : 'on target'}
                        </span>
                      </div>
                    )
                  })}
                </div>
                <p className="text-[10px] text-text-muted mt-2">Bar = current %, tick = target. Green = underweight (room to add), red = overweight.</p>
              </div>
            )}

            {/* What to buy — per-class buys spread to target, each with a
                bull case, a contrary view, and a verdict. */}
            {plan.buys && plan.buys.length > 0 && (
              <div>
                <h2 className="panel-title mb-3 flex items-center gap-2"><Target size={16} className="text-amber-400" /> What to buy</h2>
                <div className="space-y-3 stagger-children">
                  {plan.buys.map(b => (
                    <div key={b.asset_class} className="card p-5">
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <span className="font-mono font-bold text-amber-300">{b.instrument.ticker}</span>
                          <span className="text-xs text-text-muted min-w-0">{b.instrument.name}</span>
                          {b.instrument.held && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">held</span>}
                          <PortfolioFitBadge fit={b.portfolio_fit} />
                        </div>
                        <span className="text-lg font-bold tabular-nums">{fmtGBP(b.amount_gbp)}</span>
                      </div>
                      <div className="text-xs text-text-muted mb-3">{b.asset_class} · {fmtPct(b.current_pct, 0)} now → toward {fmtPct(b.target_pct, 0)} target</div>
                      <div className="space-y-2 text-sm">
                        <div className="flex gap-2"><span className="text-[10px] font-semibold uppercase text-emerald-400 mt-0.5 w-14 shrink-0">For</span><span className="text-text-secondary">{b.bull_case}</span></div>
                        <div className="flex gap-2"><span className="text-[10px] font-semibold uppercase text-rose-400 mt-0.5 w-14 shrink-0">Against</span><span className="text-text-secondary">{b.bear_case}</span></div>
                        <div className="flex gap-2 border-t border-border/30 pt-2">
                          <span className="text-[10px] font-semibold uppercase text-amber-400 mt-0.5 w-14 shrink-0">Verdict</span>
                          <span className="text-text-primary">
                            {b.verdict}
                            {b.llm_enriched && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-blue-500/15 text-blue-light align-middle" title="Condition-aware AI commentary on fixed facts">AI</span>}
                          </span>
                        </div>
                        <PortfolioFitNote fit={b.portfolio_fit} />
                      </div>
                      <div className="mt-3">
                        <Link to={`/app/stock/${encodeURIComponent(b.instrument.ticker)}`} className="btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1">
                          View {b.instrument.ticker}
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Wrapper steps — WHERE the money goes for tax (ISA/SIPP/GIA) */}
            {plan.steps.length > 0 && (
              <div className="space-y-3">
                <h2 className="panel-title mb-1 flex items-center gap-2"><Wallet size={16} className="text-amber-400" /> Which account (tax)</h2>
                {plan.steps.map(s => (
                  <div key={s.priority} className="card p-5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs flex items-center justify-center font-bold shrink-0">{s.priority}</span>
                        <span
                          title={WRAPPER_HELP[s.wrapper]}
                          className={`px-2 py-0.5 rounded border text-xs font-semibold ${WRAPPER_HELP[s.wrapper] ? 'cursor-help ' : ''}${WRAPPER_TONE[s.wrapper] ?? 'text-text-secondary border-border'}`}
                        >{s.wrapper}</span>
                        {s.account_hint && <span className="text-xs text-text-muted">→ {s.account_hint}</span>}
                      </div>
                      <span className="text-lg font-bold tabular-nums">{fmtGBP(s.amount_gbp)}</span>
                    </div>
                    <p className="text-sm text-text-secondary mt-2">{s.why}</p>
                    <p className="text-xs text-text-muted mt-1">{s.tax_note}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Idle cash ledger */}
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-border/30 panel-title">Idle cash by account</div>
              {plan.idle_accounts.map(a => (
                <div key={a.account_id} className="flex items-center justify-between px-4 py-2 border-b border-border/20 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      title={WRAPPER_HELP[a.wrapper]}
                      className={`px-1.5 py-0.5 rounded border text-[10px] shrink-0 ${WRAPPER_HELP[a.wrapper] ? 'cursor-help ' : ''}${WRAPPER_TONE[a.wrapper] ?? 'text-text-secondary border-border'}`}
                    >{a.wrapper}</span>
                    <span className="truncate">{a.account_name}</span>
                    <span className="text-text-muted text-xs shrink-0">· {a.owner}</span>
                  </div>
                  <span className="tabular-nums font-medium">{fmtGBP(a.idle_cash_gbp)}</span>
                </div>
              ))}
              {!plan.idle_accounts.length && (
                <div className="px-4 py-6 text-center text-text-muted text-sm">No idle cash — accounts are fully invested.</div>
              )}
            </div>
          </>
        )}
    </div>
  )
}
